import { Form, Input, message } from 'antd';
import { isDefined } from 'class-validator';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { IFormProps } from 'src/app/forms/form.config';
import { convertInteractionToEditFormData } from 'src/app/forms/InteractionForm/interaction-form.config';
import { getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import { convertToSelectOptionsWithId } from 'src/app/helpers/form.helper';
import { convertInterestListToSelectOptions } from 'src/app/helpers/service.helper';
import {
  useCreateInteractionMutation,
  useGetInteractionsQuery,
  useUpdateInteractionMutation,
} from 'src/app/redux/interaction/interactionSlice';
import { useGetAllInterestsQuery } from 'src/app/redux/interests/interestsSlice';
import { useGetRelationsQuery } from 'src/app/redux/relation/relationSlice';
import { INTERESTS_WHITELIST } from 'src/configs/system.config';
import { InterestClassType, InterestsTypes } from 'src/enums/interests.enum';

const InteractionForm: React.FC<IFormProps> = ({ form, recordId }) => {
  const selectedSourceInterestType = Form.useWatch<InterestsTypes>('fromInterestType', form);

  const selectedTargetInterestType = Form.useWatch<InterestsTypes>('toInterestType', form);

  const { data: editData } = useGetInteractionsQuery(undefined, {
    skip: !recordId,
    selectFromResult: ({ data }) => ({
      data: data?.rows.find((row) => row.id === recordId),
    }),
  });

  useEffect(() => {
    if (editData) {
      form.setFieldsValue(convertInteractionToEditFormData(editData));
    }
  }, [editData]);

  const { data: sourceInterestList } = useGetAllInterestsQuery(
    {
      types: [selectedSourceInterestType],
      interestsClassTypes:
        selectedSourceInterestType === InterestsTypes.Enterprise ? InterestClassType.Identified : undefined,
      customerStatus: true,
    },
    {
      skip: !isDefined(selectedSourceInterestType),
    },
  );

  const { data: targetInterestList } = useGetAllInterestsQuery(
    {
      types: [selectedTargetInterestType],
      interestsClassTypes:
        selectedSourceInterestType === InterestsTypes.Enterprise ? InterestClassType.Identified : undefined,
      customerStatus: true,
    },
    {
      skip: !isDefined(selectedTargetInterestType),
    },
  );

  const { data: relationList } = useGetRelationsQuery(
    {
      fromInterestTypes: [selectedSourceInterestType],
      toInterestTypes: [selectedTargetInterestType],
    },
    {
      skip: !(isDefined(selectedSourceInterestType) && isDefined(selectedTargetInterestType)),
    },
  );

  const [createInteraction] = useCreateInteractionMutation();
  const [updateInteraction] = useUpdateInteractionMutation();

  const onFinish = async (values: any) => {
    if (recordId) {
      updateInteraction({ ...values, id: recordId })
        .unwrap()
        .then(() => {
          message.success(t('success.edited'));
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    } else {
      createInteraction(values)
        .unwrap()
        .then(() => {
          message.success(t('success.added'));
        });
      // .catch((err) => message.error(err?.response?.data?.message || t('errors.unknownError')));
    }
  };

  return (
    <FormComponent options={{ form, onFinish }}>
      <Form.Item label={t('forms.relationIdentifiedForm.relationName')} name="name" rules={[{ required: true }]}>
        <Input variant="borderless" />
      </Form.Item>
      <Form.Item
        label={t('forms.relationIdentifiedForm.sourceInterestType')}
        name="fromInterestType"
        rules={[{ required: true }]}
      >
        <SearchableSelect
          variant="borderless"
          options={getEnumSelectOptions(InterestsTypes, INTERESTS_WHITELIST())}
          disabled={!!recordId}
        />
      </Form.Item>
      <Form.Item
        label={t('forms.relationIdentifiedForm.sourceInterest')}
        name="fromInterestId"
        rules={[{ required: true }]}
      >
        <SearchableSelect
          variant="borderless"
          // options={convertToSelectOptionsWithId(sourceInterestList || [])}
          options={convertInterestListToSelectOptions(sourceInterestList?.rows || [])}
          disabled={!!recordId}
        />
      </Form.Item>

      <Form.Item
        label={t('forms.relationIdentifiedForm.targetInterestType')}
        name="toInterestType"
        rules={[{ required: true }]}
      >
        <SearchableSelect
          variant="borderless"
          options={getEnumSelectOptions(InterestsTypes, INTERESTS_WHITELIST())}
          disabled={!!recordId}
        />
      </Form.Item>
      <Form.Item
        label={t('forms.relationIdentifiedForm.targetInterest')}
        name="toInterestId"
        rules={[{ required: true }]}
      >
        <SearchableSelect
          variant="borderless"
          options={convertInterestListToSelectOptions(targetInterestList?.rows || [])}
          disabled={!!recordId}
        />
      </Form.Item>
      <Form.Item
        label={t('forms.relationIdentifiedForm.interactionType')}
        name="interactionTypeId"
        rules={[{ required: true }]}
      >
        <SearchableSelect
          variant="borderless"
          options={convertToSelectOptionsWithId(relationList?.rows || [])}
          disabled={!!recordId}
        />
      </Form.Item>
    </FormComponent>
  );
};

export default InteractionForm;
